import React, { useContext, useEffect, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ReactComponent as HappyBuddy } from '../../../../../assets/icons/buddy-happy-open-eyes.svg';
import CustomModalNE from '../../../../../shared/components/ModalNE/CustomModalNE';
import PageHeader from '../../../../../shared/components/PageHeader/PageHeader';
import StyledButtonNE from '../../../../../shared/components/StyledButtonNE/StyledButtonNE';
import { STORY_DETAIL_ERROR_MODAL } from '../../../../../shared/constants/modal';
import { STORY_TYPES } from '../../../../../shared/constants/stories';
import { CLICK_ACTION, CONTINUE, SCREEN_STORY_DETAIL } from '../../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { ModalContext } from '../../../../../shared/context/modal/modalContext';
import { IStoryData } from '../../../../../shared/interfaces/stories';
import Stories from '../../../../../shared/services/api/communication/stories';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_STORIES_LIST } from '../../../../../shared/translations/stories';
import { GenericVoidFunction } from '../../../../../shared/types/genericTypes';
import { StoryTypeEnum } from '../../../../../shared/types/stories';
import { formatDateForDisplay } from '../../../../../shared/utils/datetime/datetime';
import './StoryDetailModal.scss';

interface StoryDetailModalProps {
  storyId: string;
  isModalOpen: boolean;
  handleCloseDetailModal: GenericVoidFunction;
}

const StoryDetailModal = ({ isModalOpen, handleCloseDetailModal, storyId }: StoryDetailModalProps) => {
  const [storyDetail, setStoryDetail] = useState<IStoryData | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const { company, isUSA } = useContext(CompanyContext);
  const { handleOpenModal } = useContext(ModalContext);

  const getStoryDetail = async () => {
    try {
      setLoading(true);
      const response = await Stories.getStoryDetail(company.id, storyId);
      setStoryDetail(response);
    } catch {
      handleCloseDetailModal();
      handleOpenModal(STORY_DETAIL_ERROR_MODAL);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    TagManagerService.pushEvent(CLICK_ACTION, CONTINUE, { ...SCREEN_STORY_DETAIL, id: storyId });
    handleCloseDetailModal();
  };

  useEffect(() => {
    getStoryDetail();
  }, [storyId]);

  return (
    <CustomModalNE open={isModalOpen} handleClose={handleCloseDetailModal}>
      <div className="story-detail-modal-container">
        <PageHeader
          PageHeaderImage={HappyBuddy}
          title={t(TRANSLATION_STORIES_LIST.modal.storyDetail.title)}
          description={t(TRANSLATION_STORIES_LIST.modal.storyDetail.description)}
        />
        <div className="story-detail-modal-info">
          {loading || !storyDetail ? (
            <>
              <Skeleton className="skeleton-story-image" variant="rectangular" />
              <div className="story-detail-modal-text">
                <Skeleton variant="text" width={150} />
                <Skeleton variant="text" width={150} />
                <Skeleton variant="text" width={150} />
                <Skeleton variant="text" width={150} />
                <Skeleton variant="text" width={150} />
                <Skeleton variant="text" width={150} />
              </div>
            </>
          ) : (
            <>
              <div className="story-detail-modal-media">
                {storyDetail.mediaType === STORY_TYPES.video && (
                  <video data-testid="sdm-video" controls muted autoPlay loop>
                    <source src={storyDetail.contentUrl} type="video/mp4" />
                    {'Sorry, your browser doesn’t support embedded videos.'}
                  </video>
                )}
                {storyDetail.mediaType === STORY_TYPES.static && (
                  <img src={storyDetail.contentUrl} alt={storyDetail.title} />
                )}
              </div>
              <div className="story-detail-modal-text">
                <div className="story-detail-modal-info-title">{storyDetail.title}</div>
                <div className="story-detail-modal-info-text">{`${t(
                  TRANSLATION_STORIES_LIST.modal.storyDetail.author
                )}: ${storyDetail.author}`}</div>
                <div className="story-detail-modal-info-text">{`${t(
                  TRANSLATION_STORIES_LIST.modal.storyDetail.type
                )}: ${
                  storyDetail.mediaType === StoryTypeEnum.image
                    ? t(TRANSLATION_STORIES_LIST.table.type.static)
                    : t(TRANSLATION_STORIES_LIST.table.type.video)
                }`}</div>
                <div className="story-detail-modal-info-text">{`${t(
                  TRANSLATION_STORIES_LIST.modal.storyDetail.dateFrom
                )}: ${formatDateForDisplay(storyDetail.startDate, isUSA)}`}</div>
                <div className="story-detail-modal-info-text">{`${t(
                  TRANSLATION_STORIES_LIST.modal.storyDetail.dateEnd
                )}: ${storyDetail.endDate ? formatDateForDisplay(storyDetail.endDate, isUSA) : '-'}`}</div>
                <div className="story-detail-modal-info-text">
                  {`${t(TRANSLATION_STORIES_LIST.modal.storyDetail.state)}:`}
                  <label className={`story-detail-modal-state state-${storyDetail.status}`}>
                    {t(TRANSLATION_STORIES_LIST.table.state[storyDetail.status])}
                  </label>
                </div>
                <div className="story-detail-modal-info-text">{`${t(
                  TRANSLATION_STORIES_LIST.modal.storyDetail.views
                )}: ${storyDetail.views}`}</div>
              </div>
            </>
          )}
        </div>
        <div className="story-detail-modal-button-container">
          <StyledButtonNE
            classes="story-detail-modal-button"
            buttonType="primary"
            disabled={loading}
            buttonSize="md"
            action={closeModal}
          >
            {t(TRANSLATION_STORIES_LIST.modal.storyDetail.button)}
          </StyledButtonNE>
        </div>
      </div>
    </CustomModalNE>
  );
};

export default StoryDetailModal;
